import { createGeotab } from "@fairfleet/geotab";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactNode, useMemo } from "react";
import { GeotabClientContext } from "@/components/GeotabClientContext";

const noop = () => {};

// A promise that never resolves — prevents queryFn from completing
// so it can never overwrite seeded data.
const NEVER = new Promise(() => {});

function createDemoGeotabClient() {
  const real = createGeotab();
  return new Proxy(real, {
    get(target, prop) {
      if (prop === "get") return () => NEVER;
      if (prop === "call") return () => NEVER;
      if (prop === "getAddresses") return () => NEVER;
      if (prop === "multiCall") return () => NEVER;
      if (prop === "set") return () => NEVER;
      if (prop === "add") return () => NEVER;
      if (prop === "remove") return () => NEVER;
      return Reflect.get(target, prop);
    },
  });
}

/**
 * Demo mode provider: mock Geotab client + isolated QueryClient.
 *
 * The mock client's API methods return promises that never resolve.
 * This means queryFn never completes, so it can never overwrite
 * data that was seeded via setQueryData. Seeded data persists forever.
 */
export function DemoQueryClientProvider({ children }: { children: ReactNode }) {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: Infinity,
            gcTime: Infinity,
            retry: false,
          },
        },
      }),
    [],
  );

  const geotabClient = useMemo(() => createDemoGeotabClient(), []);

  const contextValue = useMemo(
    () => ({
      geotabClient,
      updateGeotabClient: noop,
      switchSession: noop,
      clearGeotabClient: noop,
      username: "demo@fairfleet.de",
      database: "Demo",
      geotabView: false,
      activeSessionId: "demo",
      internalSessionId: "demo",
      sessions: ["demo"],
      isLoading: false,
      me: { id: "demo-user", name: "demo@fairfleet.de", firstName: "Demo", lastName: "User" } as any,
    }),
    [geotabClient],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <GeotabClientContext.Provider value={contextValue}>
        {children}
      </GeotabClientContext.Provider>
    </QueryClientProvider>
  );
}
